import { useAuth } from '@/hooks/use-auth';
import { useLocation } from 'wouter';
import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireFullAccount?: boolean;
}

export function ProtectedRoute({ children, requireFullAccount = false }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isFullUser } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    // If not loading and not authenticated, redirect to auth page
    if (!isLoading && !isAuthenticated) {
      // Save the current path to redirect back after login
      const currentPath = window.location.pathname + window.location.search;
      setLocation(`/auth?returnTo=${encodeURIComponent(currentPath)}`);
    }

    // If authenticated but requires full account and user is guest
    if (!isLoading && isAuthenticated && requireFullAccount && !isFullUser) {
      setLocation('/guest-dashboard');
    }
  }, [isLoading, isAuthenticated, isFullUser, requireFullAccount, setLocation]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If not authenticated, don't render anything (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  // If requires full account and user is guest, don't render
  if (requireFullAccount && !isFullUser) {
    return null;
  }

  // User is authenticated and meets requirements
  return <>{children}</>;
}